import React, { Fragment, useState, useEffect } from 'react';
import ChatGPTPresetDrawer from '../ChatGPTPresetDrawer';
import { connect } from 'react-redux';
import { toggleControl } from '../../../../store/control/duck';
import { fetchPolicy } from '../../../../store/policy/api';
import { injectIntl } from 'react-intl';
import { Grid, Typography, Box, FormControl } from '@mui/material';
import ActionButton from '../ActionButton/ActionButton';
import { decreaseGPTQuota, handleError, resetGPTQuota, validateGPTQuota } from '../../../../utils/openai';
import LoadingBackdrop from '../../../LoadingBackdrop/LoadingBackdrop';
import { useForceUpdate } from '../../../../utils/hooks';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { ClientLifeStageOptionPicker, DateRange, FreeTextArea, DISCPicker, getTraitsFromDisc } from '../Inputs/Inputs';
import EventCards from './EventCards';
import { LABEL_GAP } from '../Inputs/inputStyles';
import { getEventRecommendation } from '../../../../store/openai/apis/event-recommendation';
import { useRevealResults } from '../useRevealResults';

const ChatGPTEventRecommendationDrawer = props => {
  const { intl, chatGPTEventRecommendationDrawer, toggleControl } = props;
  const [result, setResult] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const forceUpdate = useForceUpdate();
  const { showSendButton, client, open } = chatGPTEventRecommendationDrawer || {};
  const [options, setOptions] = useState({ lifeStage: '' });
  const [traits, setTraits] = useState({});
  const [portfolios, setPortfolios] = useState([]);
  const { bodyRef, resultsRef, reveal } = useRevealResults();

  useEffect(() => {
    if (client?._id) {
      fetchPolicy({ clientId: client?._id }).then(res => {
        const portfolios = res?.portfolio || [];
        setPortfolios(portfolios);
      });
      const traits = getTraitsFromDisc(client?.factsheetId?.personality || '');
      setTraits(traits);
      setOptions(prev => ({
        ...prev,
        personality: client?.factsheetId?.personality,
        lifeStage: client?.factsheetId?.promptInput?.lifeStage
      }));
    }
  }, [client?._id, fetchPolicy]);

  const isOpen = !!chatGPTEventRecommendationDrawer && open;
  const onGetRecommendation = async () => {
    if (!client) {
      return;
    }

    try {
      validateGPTQuota();
      setLoading(true);
      const clientOptions = { ...options };
      const additionalInformation =
        options.freeText && options.freeText.trim() !== '' ? options.freeText.trim() : undefined;
      if (additionalInformation) {
        clientOptions.additionalInformation = additionalInformation;
      }
      delete clientOptions.freeText;
      const payload = {
        userObjectId: client._id,
        clientObjectId: client._id,
        factsheets: client.factsheetId,
        portfolios: portfolios,
        clientTraits: clientOptions,
        language: intl.locale,
        topN: 5,
        returnPrompt: false,
        startDate: options.startDate,
        endDate: options.endDate
      };

      const res = await getEventRecommendation(payload);
      const events = res.recommendations;
      setResult(events);
      reveal();
      decreaseGPTQuota();
    } catch (error) {
      handleError(error);
      setResult(undefined);
    } finally {
      setLoading(false);
    }
  };

  const onFeedback = () => {
    toggleControl('chatGPTFeedbackDialog', {
      client: client,
      extraContent: result,
      onSubmit: () => {
        resetGPTQuota();
        forceUpdate();
      }
    });
  };

  const handleCopyReason = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    toggleControl('chatGPTEventRecommendationDrawer', { open: false });
    setTraits({});
    setOptions({ lifeStage: '' });
    setResult(undefined);
    setLoading(false);
    setCopied(false);
  };

  const currentClientLifeStage = options.lifeStage;

  return (
    <Fragment key={props.key}>
      <FormControl>
        <LoadingBackdrop open={loading} />
        <ChatGPTPresetDrawer
          open={isOpen}
          onClose={handleClose}
          onExited={handleClose}
          onFeedback={onFeedback}
          title={intl.formatMessage({ id: 'chat-gpt-options-event-recommendation-title' })}
          subtitle={intl.formatMessage({ id: 'gpt-subtitle-event-recommendation' })}
          showQuota
          bodyRef={bodyRef}
        >
          <Grid
            container
            spacing={2}
            wrap="nowrap"
            // flexShrink 0: the drawer body is the one scroller, so this column
            // must be as tall as its content rather than squeezed to fit the body.
            style={{ minHeight: '100%', flexShrink: 0, boxSizing: 'border-box' }}
            sx={{ flexDirection: 'column' }}
          >
            {/* Description */}
            <Grid>
              <Typography style={{ whiteSpace: 'pre-wrap', color: '#666' }}>
                {intl.formatMessage({ id: 'gpt-event-recommendation-drawer-content' })}
              </Typography>
            </Grid>

            <Grid>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ClientLifeStageOptionPicker
                  intl={intl}
                  options={options}
                  onValueChange={(field, value, oldValue) => {
                    setOptions({ ...options, [field]: value });
                  }}
                />

                <DateRange
                  intl={intl}
                  options={options}
                  startField="startDate"
                  endField="endDate"
                  label="gpt-event-recommendation-date-range"
                  onValueChange={(field, value, oldValue) => {
                    setOptions(current => ({ ...current, [field]: value }));
                  }}
                  onRangeChange={(start, end) => {
                    setOptions(current => ({ ...current, startDate: start, endDate: end }));
                  }}
                />

                <FreeTextArea
                  intl={intl}
                  value={options.freeText}
                  labelKey="gpt-event-recommendation-free-text"
                  onChange={(field, value, oldValue) => {
                    setOptions({ ...options, [field]: value });
                  }}
                />
              </Box>
            </Grid>

            {/* Send Button (conditional) */}
            {showSendButton && (
              <Grid>
                <ActionButton onClick={() => {}} disabled>
                  <Typography>{intl.formatMessage({ id: 'gpt-options-product-recommendation-send' })}</Typography>
                </ActionButton>
              </Grid>
            )}

            <ActionButton onClick={onGetRecommendation} disabled={!currentClientLifeStage}>
              {intl.formatMessage({ id: 'gpt-event-recommendation-recommend-cta' })}
            </ActionButton>

            {/* Event Cards — no scroller of their own; see useRevealResults. */}
            {result && result.length > 0 && (
              <Grid ref={resultsRef}>
                <Typography style={{ color: '#333', marginBottom: LABEL_GAP }}>
                  {intl.formatMessage({ id: 'gpt-product-recommendation-reasons-title' })}
                </Typography>
                <EventCards data={result} intl={intl} />
              </Grid>
            )}

            {/* LLM Info (subtle display) */}
            {result && result.llmModel && (
              <Grid>
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px'
                  }}
                >
                  <InfoOutlined style={{ fontSize: 16, color: '#999' }} />
                  <Typography variant="caption" style={{ color: '#999' }}>
                    {result.llmModel} • {result.llmTime?.toFixed(2)}s
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Copy feedback */}
            {copied && (
              <Box
                style={{
                  position: 'fixed',
                  bottom: '80px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#333',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  zIndex: 9999
                }}
              >
                <Typography variant="body2">Copied!</Typography>
              </Box>
            )}
          </Grid>
        </ChatGPTPresetDrawer>
      </FormControl>
    </Fragment>
  );
};

const container = connect(
  state => ({
    chatGPTEventRecommendationDrawer: state.control.chatGPTEventRecommendationDrawer,
    language: state.user.language,
    clientDetails: state.client.clientDetails,
    clientData: state.client.clientData
  }),
  {
    toggleControl
  }
)(ChatGPTEventRecommendationDrawer);

export default injectIntl(container);
